import * as t from 'io-ts';

import {makeApiSerializableType, makeEnumRuntimeType} from './apiTypes';

/*
 * Runtime type.
 */

export enum BumpStrategiesEnum {
  NEVER = 'never',
  MESSAGE = 'outbound',
  COMMENT = 'comment',
  MESSAGE_AND_COMMENT = 'always',
}

export enum DefaultTeammateAccessRolesEnum {
  NONE = 'none',
  FREE_BUSY = 'free-busy',
  READER = 'reader',
}

export enum CompanyContactsScopeValuesEnum {
  NONE = 'none',
  DOUBLE_WRITE = 'double_write',
  READ_WRITE = 'read_write',
}

export enum CompanyPainPointsEnum {
  EMAIL_OVERLOAD = 'email overload',
  SLOW_RESPONSE_TIME = 'slow response time',
  MISSED_EMAILS = 'missed emails',
  DUPLICATE_EMAILS = 'duplicate emails',
  FRUSTRATED_CUSTOMERS = 'frustrated customers',
  INCOMPLETE_VIEW_OF_CUSTOMER_COMMS = 'incomplete view of customer comms',
  INEFFICIENT_NON_EXISTANT_COLLABORATION = 'inefficient/non-existant collaboration',
  SILOED_EMAILS = 'siloed emails',
  LACK_OF_OWNERSHIP = 'lack of ownership',
  LACK_OF_VISIBILITY = 'lack of visibility',
  OOO_HANDLING = 'OOO handling',
  LACK_OF_EMAIL_CATEGORIZATION = 'lack of email categorization',
  POOR_NOTIFICATIONS = 'poor notifications',
  NO_SLA_RESPONSE_TIME_TRACKING = 'no SLA/response time tracking',
  NO_SENSE_OF_ROI_FOR_COMM = 'no sense of ROI for comms',
}

export const RPainPoints = makeEnumRuntimeType(CompanyPainPointsEnum);

const RCompanyPreferencesDirty = t.readonly(
  t.type({
    canMoveToInaccessibleInbox: t.boolean,
    hasNotificationCenter: t.boolean,
    hasSso: t.boolean,
    hasInternalDiscussions: t.boolean,
    shouldLimitRecipientsDuringTrial: t.boolean,
    sendBumpStrategy: makeEnumRuntimeType(BumpStrategiesEnum),
    painPoints: t.readonlyArray(RPainPoints),
    hasAutocompleteVisitorContacts: t.boolean,
    isInvitingNewVisitorsEnabled: t.boolean,
    isUserAllowedToEnableIntegrations: t.boolean,
    canEditComments: t.boolean,
    shouldReplyWithOutlookStyle: t.boolean,
    sessionIdleTimeout: t.Integer,
    defaultTeammateAccessRole: makeEnumRuntimeType(DefaultTeammateAccessRolesEnum),
    companySize: t.union([t.Integer, t.undefined]),
    customerSuccessManagerEmail: t.union([t.string, t.undefined]),
    isCustomPricingAlertsDisabled: t.boolean,
    isBillingHidden: t.boolean,
    companyContactsScope: makeEnumRuntimeType(CompanyContactsScopeValuesEnum),
    maxTeammatesDuringTrial: t.Integer,
    salesforceAmEmail: t.union([t.string, t.null]),
    skipSpfDkimWarnings: t.boolean,
  }),
);

/*
 * Type.
 */

export interface CompanyPreferencesModel extends t.TypeOf<typeof RCompanyPreferencesDirty> {}
export const RCompanyPreferences = t.clean<CompanyPreferencesModel>(RCompanyPreferencesDirty as any);

/*
 * IO methods.
 */

export function parseCompanyPreferences(src: any): CompanyPreferencesModel {
  return {
    canMoveToInaccessibleInbox: Boolean(src.canMoveToInaccessibleInbox),
    painPoints: src.hypothesizedPains || [],
    hasNotificationCenter: Boolean(src.enableNotificationCenter),
    hasSso: Boolean(src.sso && src.sso.enabled),
    shouldLimitRecipientsDuringTrial: Boolean(src.limitRecipientsDuringTrial),
    hasInternalDiscussions: Boolean(src.discussion),
    sendBumpStrategy: src.bumpOnCommentAndOutbound,
    hasAutocompleteVisitorContacts: Boolean(src.autocompleteMentionContacts),
    isInvitingNewVisitorsEnabled: Boolean(src.supportsVisitors),
    isUserAllowedToEnableIntegrations: Boolean(src.isUserAllowedToEnableIntegrations),
    canEditComments: Boolean(src.canEditComments),
    shouldReplyWithOutlookStyle: Boolean(src.outlookStyleReply),
    sessionIdleTimeout: src.sessionIdleTimeout || 0,
    defaultTeammateAccessRole: src.defaultTeammateCalendarAccessRole || DefaultTeammateAccessRolesEnum.READER,
    companySize: src.companySize || undefined,
    customerSuccessManagerEmail: src.salesforceCsmEmail || undefined,
    isCustomPricingAlertsDisabled: Boolean(src.disableCustomPricingAlerts),
    isBillingHidden: Boolean(src.hideBilling),
    companyContactsScope: src.companyContactsScope || CompanyContactsScopeValuesEnum.NONE,
    maxTeammatesDuringTrial: src.maxTeammatesDuringTrial || 10,
    salesforceAmEmail: src.salesforceAmEmail || null,
    skipSpfDkimWarnings: src.skipSpfDkimWarnings ?? false,
  };
}

export function serializeCompanyPreferences(companyPreferences: CompanyPreferencesModel): any {
  return {
    canMoveToInaccessibleInbox: companyPreferences.canMoveToInaccessibleInbox,
    companySize: companyPreferences.companySize,
  };
}

/*
 * API type.
 */

export const ApiCompanyPreferences = makeApiSerializableType(
  'companyPreferences',
  RCompanyPreferences,
  parseCompanyPreferences,
  serializeCompanyPreferences,
);
